import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import Popover from '@mui/material/Popover'
import { toast } from 'react-toastify'
import fetcher from '../../../fetcher'
import { CrmTopbarPortal, useInCrmTopbar } from '../CrmLayout'
import { useOpenPerson } from '../hooks/useOpenPerson'
import { ownerColor } from '../PipelinePage/OpportunityCard'
import { ConfirmDialog } from '../PersonDetailPage/ConfirmDialog'
import { QuoteCard } from '../PersonDetailPage/QuoteCard'
import { VehicleCardBody } from '../PersonDetailPage/VehicleCardBody'
import { vehicleToQuote } from '../PersonDetailPage/vehicleToQuote'
import { wonOppToQuote } from '../PersonDetailPage/wonOppToQuote'
import { HouseholdModal } from '../HouseholdModal'
import '../PersonDetailPage/PersonDetailPage.scss'
import './HouseholdDetailPage.scss'

// People's Households tab, where Back and Delete land.
const HOUSEHOLDS_TAB_STATE = { tab: 'households' }

const initials = (name) => {
    const parts = (name || '?').trim().split(/\s+/)
    return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)).toUpperCase()
}

// Every page of a CRM list, as Person Detail reads its opportunities and cars.
const allPages = async (get, filters) => {
    const all = []
    for (let page = 1; ; page += 1) {
        const res = await get(new URLSearchParams({ ...filters, page: String(page) }))
        const { results = [], has_more } = res?.data || {}
        all.push(...results)
        if (!has_more) return all
    }
}

// Person Detail's money: whole dollars, $0 when there is nothing.
const money = (v) => {
    const n = Number(v)
    if (!v || Number.isNaN(n)) return '$0'
    return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

// A member's status now. Someone who went dead stays a member and says so.
const memberStatus = (m) => (m.is_client ? ['Client', 'tag-client'] : m.is_lead ? ['Lead', 'tag-lead'] : ['Dead Lead', 'tag-contact'])

// Full-page Household (route /crm/people/households/:id), laid out like Person
// Detail: members where the contact card is, the household's cars beside them.
// What the viewer may do comes from the server's can_manage / can_delete.
export const HouseholdDetailPage = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const openPerson = useOpenPerson()
    const inBar = useInCrmTopbar()
    const [household, setHousehold] = useState(null)
    const [loading, setLoading] = useState(true)
    const [draftName, setDraftName] = useState('')
    const [removing, setRemoving] = useState(null)
    const [adding, setAdding] = useState(false)
    const [menuAnchor, setMenuAnchor] = useState(null)
    const [confirmingDelete, setConfirmingDelete] = useState(false)
    const [vehicles, setVehicles] = useState([])
    const [sales, setSales] = useState([])
    const [carOwner, setCarOwner] = useState('all')
    const revertingRef = useRef(false)
    // Bumped by every load; only the newest one's reply is shown, so a slow
    // load can't put back a member just removed, or show the household this
    // page showed before.
    const latestRef = useRef(0)

    // The household with its visible members' cars and sales: the lists Person
    // Detail reads, filtered to the household server-side, so one request each.
    const load = useCallback(async () => {
        const seq = ++latestRef.current
        try {
            const [res, cars, won] = await Promise.all([
                fetcher.getCrmHousehold(id),
                // As Person Detail: a vehicles outage costs only the hand-entered
                // cars, but failed sales fail the load rather than show $0.
                allPages(fetcher.getCrmVehicles, { household: id }).catch(() => []),
                allPages(fetcher.getCrmOpportunities, { household: id, status: 'WON' }),
            ])
            if (seq === latestRef.current) {
                setHousehold(res.data)
                setDraftName(res.data.name)
                setVehicles(cars)
                setSales(won)
            }
        } catch (e) {
            // A 404 is redirected by the interceptor; anything else is toasted.
        } finally {
            if (seq === latestRef.current) setLoading(false)
        }
    }, [id])

    useEffect(() => {
        setLoading(true)
        setHousehold(null)
        load()
    }, [load])

    // Back pops to wherever the household was opened from, or to the
    // Households tab when there is nothing to pop (a pasted URL, a new tab).
    const goBack = () => {
        if (location.key === 'default') navigate('/crm/people', { state: HOUSEHOLDS_TAB_STATE })
        else navigate(-1)
    }

    // Inline rename, as Person Detail renames a contact: Enter or blur saves,
    // Escape (or a blank name) puts the saved one back.
    const commitName = async () => {
        if (revertingRef.current) {
            revertingRef.current = false
            return
        }
        const name = draftName.trim()
        if (!name || name === household.name) {
            setDraftName(household.name)
            return
        }
        try {
            const res = await fetcher.updateCrmHousehold(household.id, { name })
            setHousehold((h) => ({ ...h, name: res.data.name }))
            setDraftName(res.data.name)
        } catch (e) {
            setDraftName(household.name)
        }
    }
    const nameKeyDown = (e) => {
        if (e.key === 'Enter') e.target.blur()
        if (e.key === 'Escape') {
            revertingRef.current = true
            setDraftName(household.name)
            e.target.blur()
        }
    }

    // A membership change, not a deletion, and one Add puts back: no confirm.
    const removeMember = async (member) => {
        if (removing) return
        setRemoving(member.id)
        try {
            await fetcher.removeCrmHouseholdMembers(household.id, [member.id])
            toast.success(`Removed ${member.name || 'Unnamed'} from ${household.name}.`)
        } catch (e) {
            // The interceptor toasts the server's reason.
        } finally {
            // Members, their cars and the profit move together: reload them all.
            await load()
            setRemoving(null)
        }
    }

    const deleteHousehold = async () => {
        setConfirmingDelete(false)
        try {
            await fetcher.deleteCrmHousehold(household.id)
            toast.success(`Deleted ${household.name}.`)
            navigate('/crm/people', { replace: true, state: HOUSEHOLDS_TAB_STATE })
        } catch (e) {
            // The interceptor toasts why (e.g. another salesperson's people are in it).
        }
    }

    const backButton = (
        <button className="cdv-back" onClick={goBack} aria-label="Back">
            <ArrowBackIcon style={{ fontSize: 18 }} />
        </button>
    )

    const canManage = Boolean(household?.can_manage)
    const members = household?.members || []

    // Person Detail's rules, over every visible member: Lifetime Profit sums
    // the won sales (a car since removed still counts); Current Vehicles are the
    // hand-recorded cars, then the sales whose car is still theirs.
    const lifetimeProfit = sales.reduce((s, o) => s + (Number(o.total_profit) || 0), 0)
    const memberName = Object.fromEntries(members.map((m) => [m.id, m.name || 'Unnamed']))
    const cars = [
        ...vehicles.map((v) => ({ key: `v${v.id}`, personId: v.person, quote: vehicleToQuote(v) })),
        ...sales.filter((o) => !o.vehicle_removed_at)
            .map((o) => ({ key: `o${o.id}`, personId: o.person, quote: wonOppToQuote(o) })),
    ]
    const carOwners = members.filter((m) => cars.some((c) => c.personId === m.id))
    // A filter on someone no longer here falls back to everyone.
    const shownOwner = carOwners.some((m) => m.id === carOwner) ? carOwner : 'all'
    const shownCars = shownOwner === 'all' ? cars : cars.filter((c) => c.personId === shownOwner)

    return (
        <div className="cdv cdv-full">
            <CrmTopbarPortal
                fallback={(
                    <div className="subheader">
                        {!loading && backButton}
                        <span className="bc-current">{household?.name || (loading ? 'Loading…' : 'Household')}</span>
                        {household && <span className="id-badge id-badge-hh">Household</span>}
                    </div>
                )}
            >
                {null}
            </CrmTopbarPortal>

            {!household ? (
                <div className="cd-loading">{inBar && !loading && backButton}{loading ? 'Loading…' : 'Not found.'}</div>
            ) : (
                <div className={`cpage${inBar ? ' with-back' : ''}`}>
                    {inBar && backButton}
                    <div className="left-col">
                        <div className="panel">
                            <div className="panel-body">
                                <div className="contact-head">
                                    <div className="contact-head-main">
                                        <input
                                            className="contact-name-input"
                                            value={draftName}
                                            onChange={(e) => setDraftName(e.target.value)}
                                            onBlur={commitName}
                                            onKeyDown={nameKeyDown}
                                            readOnly={!canManage}
                                            maxLength={255}
                                            aria-label="Household name"
                                        />
                                        <div className="contact-stage-row">
                                            <span className="stage-pill hh-tag">Household</span>
                                        </div>
                                    </div>
                                    {household.can_delete && (
                                        <button
                                            type="button"
                                            className="person-actions-btn"
                                            aria-label="More actions"
                                            aria-haspopup="menu"
                                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                                        >
                                            <MoreHorizIcon style={{ fontSize: 18 }} />
                                        </button>
                                    )}
                                </div>

                                <div className="contact-field hh-profit">
                                    <div className="field-label">Lifetime Profit</div>
                                    <input className="contact-field-input" value={money(lifetimeProfit)} aria-label="Lifetime Profit" readOnly />
                                </div>
                                <div className="contact-divider" />

                                <div className="hh-members-head">
                                    <span className="field-label">Members · {household.member_count}</span>
                                    {canManage && (
                                        <button type="button" className="cbtn-secondary-sm" onClick={() => setAdding(true)}>
                                            + Add member
                                        </button>
                                    )}
                                </div>
                                {members.length ? (
                                    <div className="hh-members">
                                        {members.map((m) => {
                                            const [status, tag] = memberStatus(m)
                                            return (
                                                <div key={m.id} className="hh-member">
                                                    <button type="button" className="hh-member-open" onClick={() => openPerson(m.id)}>
                                                        <span className="hh-member-av" style={{ background: ownerColor(m.owner_detail?.id) }}>
                                                            {initials(m.name)}
                                                        </span>
                                                        <span className="hh-member-main">
                                                            <span className="hh-member-name">{m.name || 'Unnamed'}</span>
                                                            {m.owner_detail?.name && <span className="hh-member-sub">{m.owner_detail.name}</span>}
                                                        </span>
                                                        <span className={`nl-suggest-tag ${tag}`}>{status}</span>
                                                    </button>
                                                    {canManage && (
                                                        <button
                                                            type="button"
                                                            className="hh-member-x"
                                                            onClick={() => removeMember(m)}
                                                            disabled={removing !== null}
                                                            aria-label={`Remove ${m.name || 'Unnamed'} from household`}
                                                            title="Remove from household"
                                                        >
                                                            <CloseIcon />
                                                        </button>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    <div className="hh-empty">
                                        No members yet.{canManage && ' Add the clients and current leads who belong to this household.'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="right-col">
                        <div className="panel">
                            <div className="quotes-header">
                                <div className="panel-title">Current Vehicles</div>
                                {carOwners.length > 1 && (
                                    <div className="chip-row hh-car-filter" role="group" aria-label="Show cars of">
                                        {[{ id: 'all', name: 'All' }, ...carOwners].map((m) => (
                                            <button
                                                key={m.id}
                                                type="button"
                                                className={`chip${shownOwner === m.id ? ' active' : ''}`}
                                                aria-pressed={shownOwner === m.id}
                                                onClick={() => setCarOwner(m.id)}
                                            >
                                                {m.id === 'all' ? 'All' : (m.name || 'Unnamed').trim().split(/\s+/)[0]}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="quotes-body">
                                {shownCars.length ? (
                                    <div className="quotes-grid">
                                        {/* Person Detail's vehicle cards, each naming its member; a
                                            car is edited from its member's page, which a click opens. */}
                                        {shownCars.map(({ key, personId, quote }) => (
                                            <QuoteCard
                                                key={key}
                                                quote={{ ...quote, note: memberName[personId] }}
                                                vehicleBody={<VehicleCardBody quote={quote} />}
                                                showStatus={false}
                                                onCardClick={() => openPerson(personId)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-mini">
                                        {members.length ? 'No current vehicles for these members.' : 'Members’ current vehicles show here.'}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Popover
                open={Boolean(menuAnchor)}
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <div
                    className="qcard-menu-item is-danger"
                    onClick={() => { setMenuAnchor(null); setConfirmingDelete(true) }}
                >
                    Delete household
                </div>
            </Popover>

            {household && (
                <ConfirmDialog
                    open={confirmingDelete}
                    title={`Delete ${household.name}?`}
                    message="This deletes the household only. Its members, their vehicles and their deals stay in the CRM."
                    confirmLabel="Delete household"
                    onCancel={() => setConfirmingDelete(false)}
                    onConfirm={deleteHousehold}
                />
            )}

            {household && (
                <HouseholdModal
                    open={adding}
                    existing={household}
                    onClose={() => setAdding(false)}
                    onDone={() => { setAdding(false); load() }}
                />
            )}
        </div>
    )
}
